import React from 'react';

/* Shared visual language for the admin panel: one place to change the look. */

export const STATUS_STYLES = {
  // CVs
  pending: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  reviewed: 'bg-sky-50 text-sky-700 ring-sky-600/20',
  shortlisted: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  rejected: 'bg-rose-50 text-rose-700 ring-rose-600/20',
  // Contacts
  new: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
  read: 'bg-slate-100 text-slate-600 ring-slate-500/20',
  replied: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  archived: 'bg-slate-100 text-slate-500 ring-slate-500/20',
  // Consultations
  contacted: 'bg-sky-50 text-sky-700 ring-sky-600/20',
  scheduled: 'bg-violet-50 text-violet-700 ring-violet-600/20',
  completed: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  // Jobs
  draft: 'bg-slate-100 text-slate-600 ring-slate-500/20',
  active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  closed: 'bg-rose-50 text-rose-700 ring-rose-600/20'
};

export const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize ring-1 ring-inset ${
      STATUS_STYLES[status] || 'bg-slate-100 text-slate-600 ring-slate-500/20'
    }`}
  >
    {String(status || '—').replace(/-/g, ' ')}
  </span>
);

export const Card = ({ className = '', children, ...rest }) => (
  <div
    className={`rounded-2xl border border-slate-200/80 bg-white shadow-sm ${className}`}
    {...rest}
  >
    {children}
  </div>
);

const BUTTON_VARIANTS = {
  primary: 'bg-indigo-600 text-white hover:bg-indigo-700 focus-visible:outline-indigo-600 shadow-sm',
  secondary: 'bg-white text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50',
  danger: 'bg-rose-600 text-white hover:bg-rose-700 focus-visible:outline-rose-600 shadow-sm',
  ghost: 'text-slate-600 hover:bg-slate-100'
};

export const Button = ({ variant = 'secondary', className = '', children, ...rest }) => (
  <button
    type="button"
    className={`inline-flex items-center justify-center gap-2 rounded-xl px-3.5 py-2 text-sm font-medium transition
      focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
      disabled:cursor-not-allowed disabled:opacity-50 ${BUTTON_VARIANTS[variant]} ${className}`}
    {...rest}
  >
    {children}
  </button>
);

export const Spinner = ({ className = 'h-5 w-5' }) => (
  <svg className={`animate-spin text-indigo-600 ${className}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

export const EmptyState = ({ title, description, icon = '📭', action }) => (
  <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-2xl">
      {icon}
    </div>
    <p className="text-base font-semibold text-slate-900">{title}</p>
    {description && <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Field = ({ label, children, className = '' }) => {
  const isEmpty = children === null || children === undefined || children === '';
  return (
    <div className={className}>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className={`mt-1 text-sm ${isEmpty ? 'text-slate-300' : 'text-slate-900'}`}>
        {isEmpty ? '—' : children}
      </dd>
    </div>
  );
};

/* Formatting helpers shared across pages */

export const formatDate = (value, withTime = false) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {})
  });
};

export const relativeTime = (value) => {
  if (!value) return '';
  const diff = Date.now() - new Date(value).getTime();
  if (Number.isNaN(diff)) return '';
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(value);
};

export const GENDER_LABELS = {
  male: 'Male',
  female: 'Female',
  'prefer-not-to-say': 'Prefer not to say'
};

export const DEGREE_LABELS = {
  bachelor: "Bachelor's Degree",
  master: "Master's Degree",
  mba: 'MBA',
  phd: 'PhD',
  diploma: 'Diploma'
};
